const STORAGE_KEY = "huskly-cookie-consent";

class SimpleCookieConsent {
  constructor({
    title = "Cookie preferences",
    description = "We use cookies to understand site usage and improve your experience.",
  } = {}) {
    this.title = title;
    this.description = description;
    this.acceptedCategories = [];
    this.listeners = new Set();
    this.banner = null;
    const saved = this.loadPreferences();
    if (saved) {
      this.acceptedCategories = saved.acceptedCategories || [];
      this.hasRecordedChoice = true;
    } else {
      this.hasRecordedChoice = false;
    }
    if (typeof document !== "undefined") {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.mount());
      } else {
        this.mount();
      }
    }
  }

  on(event, callback) {
    if (event !== "change" || typeof callback !== "function") return () => {};
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  emitChange() {
    this.listeners.forEach((cb) => {
      try {
        cb(this);
      } catch (error) {
        console.error("Cookie consent listener failed", error);
      }
    });
  }

  acceptAll() {
    this.setCategories(["analytics"]);
  }

  rejectAll() {
    this.setCategories([]);
  }

  setCategories(categories) {
    this.acceptedCategories = categories;
    this.hasRecordedChoice = true;
    this.persistPreferences();
    this.hide();
    this.emitChange();
  }

  loadPreferences() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.warn("Unable to load cookie preferences", error);
      return null;
    }
  }

  persistPreferences() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ acceptedCategories: this.acceptedCategories }),
      );
    } catch (error) {
      console.warn("Unable to persist cookie preferences", error);
    }
  }

  mount() {
    if (this.hasRecordedChoice || this.banner) return;
    const container = document.createElement("div");
    container.style.cssText = `
      position: fixed;
      bottom: 16px;
      left: 16px;
      right: 16px;
      z-index: 60;
      display: flex;
      justify-content: center;
      pointer-events: none;
    `;

    const card = document.createElement("div");
    card.style.cssText = `
      width: min(960px, 100%);
      background: #0f172a;
      color: #e2e8f0;
      border-radius: 14px;
      box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
      padding: 16px 18px;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: center;
      pointer-events: auto;
      font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;

    const copy = document.createElement("div");
    copy.style.cssText = `
      flex: 1 1 240px;
      min-width: 0;
    `;
    if (this.title) {
      const heading = document.createElement("div");
      heading.textContent = this.title;
      heading.style.cssText =
        "font-weight: 700; margin-bottom: 4px; font-size: 15px;";
      copy.appendChild(heading);
    }
    const description = document.createElement("p");
    description.textContent = this.description;
    description.style.cssText =
      "opacity: 0.92; font-size: 14px; line-height: 1.5; margin: 0;";
    copy.appendChild(description);

    const actions = document.createElement("div");
    actions.style.cssText = `
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
      flex: 1 1 200px;
    `;

    const rejectButton = document.createElement("button");
    rejectButton.type = "button";
    rejectButton.textContent = "Reject";
    rejectButton.style.cssText = `
      border: 1px solid #334155;
      background: transparent;
      color: #e2e8f0;
      padding: 10px 14px;
      border-radius: 10px;
      font-weight: 600;
      cursor: pointer;
      transition: all 120ms ease;
    `;
    rejectButton.addEventListener("mouseenter", () => {
      rejectButton.style.borderColor = "#94a3b8";
    });
    rejectButton.addEventListener("mouseleave", () => {
      rejectButton.style.borderColor = "#334155";
    });
    rejectButton.addEventListener("click", () => this.rejectAll());

    const acceptButton = document.createElement("button");
    acceptButton.type = "button";
    acceptButton.textContent = "Accept analytics";
    acceptButton.style.cssText = `
      border: none;
      background: linear-gradient(135deg, #22c55e, #10b981);
      color: #0b1120;
      padding: 10px 16px;
      border-radius: 10px;
      font-weight: 700;
      cursor: pointer;
      transition: transform 120ms ease, box-shadow 120ms ease;
      box-shadow: 0 10px 30px rgba(16, 185, 129, 0.45);
    `;
    acceptButton.addEventListener("mouseenter", () => {
      acceptButton.style.transform = "translateY(-1px)";
    });
    acceptButton.addEventListener("mouseleave", () => {
      acceptButton.style.transform = "translateY(0)";
    });
    acceptButton.addEventListener("click", () => this.acceptAll());

    actions.append(rejectButton, acceptButton);
    card.append(copy, actions);
    container.appendChild(card);
    document.body.appendChild(container);
    this.banner = container;
  }

  hide() {
    if (!this.banner) return;
    this.banner.remove();
    this.banner = null;
  }
}

export default SimpleCookieConsent;
